import React, { useEffect, useMemo, useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { rankItem } from "@tanstack/match-sorter-utils";
import { InputGroup, Form, Tab, Button, Modal } from "react-bootstrap";
import { useDataFetch } from "./../../hooks/useDataFetch";
import StudentCreate from "../components/Student/Create";

import { useNavigate } from "react-router-dom";
import StudentConfig from "../components/StudentConfig/Create";
import { useDegrees } from "../../hooks/useDegrees";

const fuzzyFilter = (row, columnId, value) => {
  return rankItem(row.getValue(columnId), value).passed;
};

const GlobalFilter = ({ globalFilter, setGlobalFilter }) => {
  return (
    <InputGroup className="mb-3">
      <Form.Control
        placeholder="Buscar"
        value={globalFilter ?? ""}
        onChange={(e) => setGlobalFilter(e.target.value || undefined)}
      />
    </InputGroup>
  );
};

const Student = () => {
  const navigate = useNavigate();
  const [globalFilter, setGlobalFilter] = useState("");
  const [yearFilter, setYearFilter] = useState(
    new Date().getFullYear().toString()
  );
  const [degreeFilter, setDegreeFilter] = useState("");
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [newStudent, setNewStudent] = useState(false);
  const [configStudent, setConfiStudent] = useState(false);
  const [studentId, setStudentId] = useState();

  const { getData, invalidateQuery } = useDataFetch();

  const { status, data, error, isFetching, refetch } = getData(
    ["students"],
    "/students",
    { enabled: true },
    { year: yearFilter, degree: degreeFilter }
  );

  const { degrees: datadegreeList } = useDegrees(yearFilter);

  const columns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Tipo de identificación",
      },
      {
        accessorKey: "identification",
        header: "Identificación",
      },
      {
        accessorFn: (row) => `${row.first_name} ${row.last_name}`,
        id: "full_name",
        header: "Nombre",
      },
      {
        accessorKey: "email",
        header: "Correo",
        cell: ({ getValue }) => getValue() || "-",
      },
      {
        accessorKey: "cellphone",
        header: "Celular",
        cell: ({ getValue }) => getValue() || "-",
      },
      {
        accessorFn: (row) => `${row.level || "-"} ${row.degree || "-"}`,
        id: "grade",
        header: "Grado",
      },
      {
        accessorKey: "status",
        header: "Estado",
        cell: ({ getValue }) => (getValue() === 1 ? "Activo" : "Inactivo"),
      },
      {
        id: "options",
        header: "",
        cell: ({ row }) => (
          <div className="d-flex gap-10">
            <i
              className="flaticon-381-settings-6"
              style={{ fontSize: 20, cursor: "pointer" }}
              onClick={() => {
                setStudentId(row.original.id);
                setConfiStudent(true);
              }}
            ></i>
            <i
              className="flaticon-381-id-card-1"
              style={{ fontSize: 20, cursor: "pointer", marginLeft: 10 }}
              onClick={() => {
                navigate(`/settlement/${row.original.id}/${yearFilter}`);
              }}
            ></i>
          </div>
        ),
      },
    ],
    [yearFilter]
  );

  const table = useReactTable({
    data: data?.data ?? [],
    columns,
    state: {
      globalFilter,
      pagination,
    },
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter, // Aplicar filtro fuzzy para el filtro global
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
  });

  useEffect(() => {
    setDegreeFilter("");
    refetch();
  }, [yearFilter]);

  useEffect(() => {
    refetch();
  }, [degreeFilter]);

  return (
    <Tab.Container defaultActiveKey="all">
      <div className="d-flex justify-content-between">
        <div className="d-flex">
          <select
            className="form-control mb-3"
            onChange={(e) => setYearFilter(e.target.value)}
          >
            {[2025, 2026, 2027, 2028, 2029, 2030].map((year) => (
              <option
                key={year}
                selected={yearFilter == year.toString()}
                value={year}
              >
                {year}
              </option>
            ))}
          </select>
          <select
            className="form-control mb-3 ml-3"
            onChange={(e) => setDegreeFilter(e.target.value)}
            value={degreeFilter}
            style={{ width: 200 }}
          >
            <option value="">Todos los grados</option>
            {datadegreeList?.map((e) => (
              <option key={e.id} value={e.degree}>
                {e.level} {e.degree}
              </option>
            ))}
          </select>
        </div>

        <div className="d-flex" style={{ width: 500 }}>
          <GlobalFilter
            globalFilter={globalFilter}
            setGlobalFilter={setGlobalFilter}
          />
          <Button
            className="btn btn-primary text-nowrap mb-3 ml-3"
            onClick={() => setNewStudent(true)}
          >
            <i className="fa fa-plus scale5 mr-3" aria-hidden="true" />
            Nuevo
          </Button>
        </div>
      </div>

      <div className="table-responsive">
        <table className="table card-table display dataTablesCard dataTable no-footer">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div className="d-sm-flex text-center justify-content-between align-items-center mt-3">
          <div className="dataTables_info">
            Mostrando{" "}
            {table.getRowModel().rows.length *
              (table.getState().pagination.pageIndex + 1)}{" "}
            de {table.getRowCount().toLocaleString()} registros
          </div>
          <div
            className="dataTables_paginate paging_simple_numbers"
            id="example2_paginate"
          >
            <Button
              className="paginate_button previous"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              Anterior
            </Button>

            <Button
              className="paginate_button next"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              Siguiente
            </Button>
          </div>
        </div>
      </div>
      <Modal className="fade bd-example-modal-lg" show={newStudent} size="xl">
        <Modal.Header>
          <Modal.Title>Nuevo Estudiante</Modal.Title>
          <Button
            variant=""
            className="close"
            onClick={() => setNewStudent(false)}
          >
            <span>&times;</span>
          </Button>
        </Modal.Header>
        <Modal.Body>
          <StudentCreate
            refetchStudent={() => {
              invalidateQuery("students");
              refetch();
            }}
          />
        </Modal.Body>
      </Modal>

      <Modal
        className="fade bd-example-modal-lg"
        show={configStudent}
        size="xl"
      >
        <Modal.Header>
          <Modal.Title>Configuración del Estudiante</Modal.Title>
          <Button
            variant=""
            className="close"
            onClick={() => setConfiStudent(false)}
          >
            <span>&times;</span>
          </Button>
        </Modal.Header>
        <Modal.Body>
          <StudentConfig year={yearFilter} id={studentId} />
        </Modal.Body>
      </Modal>
    </Tab.Container>
  );
};

export default Student;
